import { MAX_MODIFIER_QUANTITY } from '../constants/modifierQuantity';
import { ParsedModifierGroup } from '../types/menu';
import { findSlotFamilies } from './slotFamilies';

/**
 * One slot of a brand-menu constraint the parser split into `<name>_1..N`
 * single-pick groups (`min === max > 1`, the Baker's Dozen `bagel_choice`).
 * The split keeps `name` bare and suffixes `id`, which is what tells a slot
 * from a constraint that merely ends in a number.
 */
export const isSplitSlotGroup = (
  modGroup: Pick<ParsedModifierGroup, 'id' | 'name'>
): boolean => {
  const id = String(modGroup.id ?? '');
  const prefix = `${modGroup.name}_`;
  return id.startsWith(prefix) && /^\d+$/.test(id.slice(prefix.length));
};

type QuantityRuleGroup = Pick<
  ParsedModifierGroup,
  'id' | 'name' | 'uniqueName' | 'maximumSelections' | 'maxPerOption'
>;

/**
 * Whether a group is one unit's slot: the front end's own split (above), or a
 * `_1..N` family the brand menu defines constraint by constraint (a Crowd
 * Pleaser's `chili_dog_selection_1..4`), found among its `siblings` — the other
 * groups on the same level — by the rule the repeated-slot panel uses.
 */
export const isSlotGroup = (
  modGroup: QuantityRuleGroup,
  siblings: QuantityRuleGroup[] = []
): boolean => {
  if (isSplitSlotGroup(modGroup)) return true;
  return Array.from(findSlotFamilies(siblings).values()).some(
    (family) =>
      family.length > 1 &&
      family.some((member) => member.modGroup.id === modGroup.id)
  );
};

/** A group's selections, keyed by option id, as far as counting needs them. */
type SelectedOptions = Record<string, { quantity?: number }>;

/**
 * HITL-1762 — the constraint's `max_per_option`: a whole number of at least 1,
 * or null. Null (the brand-menu default) means no per-option cap, and `max`
 * counts units; anything else means `max` counts different options.
 */
export const getMaxPerOption = (
  modGroup: Pick<ParsedModifierGroup, 'maxPerOption'>
): number | null => {
  const value = modGroup.maxPerOption;
  return typeof value === 'number' && value >= 1 ? Math.floor(value) : null;
};

/** Units in a group: each selection counts its quantity, one when unset. */
export const countModifierUnits = (
  selectedItems: SelectedOptions = {},
  exceptOptionId?: string
): number =>
  Object.entries(selectedItems).reduce(
    (units, [id, option]) =>
      id === exceptOptionId ? units : units + (option.quantity || 1),
    0
  );

/**
 * HITL-1756 — the most of one option this group could ever take. The one place
 * the brand menu's rule lives (HITL-1762, agreed with gpt-context and Menu
 * Module on PRVA-14591).
 *
 * - Brand menu only: the count travels as the modifier's `quantity` in the
 *   Menu Module order, which only the brand-menu path builds.
 * - Never on a slot ({@link isSlotGroup}): a dozen is counted by filling
 *   slots, which the repeated-slot panel owns. A count on one slot would send
 *   "4 plain" as the third bagel, and "×3" on chili dog 2's selection means
 *   nothing.
 * - `max_per_option` set: that number (sweetener max 1, per option 9).
 * - Null: `max` counts units, so one option can take all of it (dips max 2 →
 *   2x ranch). An unlimited group (-1) gets {@link MAX_MODIFIER_QUANTITY}.
 *
 * A size group (max 1, null) comes out at 1, which is what keeps the stepper
 * off it.
 */
export const getModifierMaxQuantity = (
  modGroup: QuantityRuleGroup,
  isBrandMenu: boolean,
  /** The other groups on the group's level, to recognise a slot family. */
  siblings?: QuantityRuleGroup[]
): number => {
  if (!isBrandMenu || isSlotGroup(modGroup, siblings)) return 1;
  const perOption = getMaxPerOption(modGroup);
  if (perOption !== null) return perOption;
  const max = modGroup.maximumSelections;
  return max > 0 ? max : MAX_MODIFIER_QUANTITY;
};

/**
 * HITL-1762 — the most of `optionId` the agent can ring right now. Under a null
 * `max_per_option` the other options share `max`: with ranch and mayo both in a
 * max-2 dip group, neither can go to 2.
 */
export const getModifierQuantityCap = (
  modGroup: QuantityRuleGroup,
  isBrandMenu: boolean,
  selectedItems: SelectedOptions | undefined,
  optionId: string,
  siblings?: QuantityRuleGroup[]
): number => {
  const cap = getModifierMaxQuantity(modGroup, isBrandMenu, siblings);
  const max = modGroup.maximumSelections;
  if (cap <= 1 || getMaxPerOption(modGroup) !== null || !(max > 0)) return cap;
  return Math.max(
    1,
    Math.min(cap, max - countModifierUnits(selectedItems, optionId))
  );
};

/**
 * HITL-1762 — whether a multi-select group has no room for one more option.
 * Counts units under a null `max_per_option`, different options otherwise.
 */
export const isModGroupFullForNewOption = (
  modGroup: Pick<ParsedModifierGroup, 'maximumSelections' | 'maxPerOption'>,
  selectedItems: SelectedOptions = {}
): boolean => {
  const max = modGroup.maximumSelections;
  if (!(max > 0)) return false;
  const taken =
    getMaxPerOption(modGroup) !== null
      ? Object.keys(selectedItems).length
      : countModifierUnits(selectedItems);
  return taken >= max;
};

/**
 * HITL-1762 — whether the count rule is in force. `isInvalidModGroup` is read
 * from deep inside cart utilities that have no store, so the gate
 * (`selectIsModifierQuantityEnabled`) is mirrored here by `useModifierCountRule`.
 * Off, validation counts different options exactly as it always has.
 */
let isCountRuleActive = false;

export const setModifierCountRuleActive = (active: boolean): void => {
  isCountRuleActive = active;
};

export const isModifierCountRuleActive = (): boolean => isCountRuleActive;

export const clampModifierQuantity = (quantity: number, max: number): number =>
  Math.min(Math.max(Math.round(quantity) || 1, 1), max);
